<template>
  <div>
    <BaseReportTable
      :resultados="resultados"
      :total-items="totalItems"
      :items-por-pagina="itemsPorPagina"
      :pagina-actual="paginaActual"
      :loading="loading"
      :headers="infoCobranzasHeaders"
      :es-operacion="esOperacion"
      @cambiar-pagina="$emit('cambiar-pagina', $event)"
      @cambiar-items-por-pagina="$emit('cambiar-items-por-pagina', $event)"
    >
      <template v-slot:item.legajo="{ item }">
        <a
          v-if="item.legajo"
          :href="getGestionarUrl(item.legajo)"
          target="_blank"
          class="legajo-link"
        >
          {{ item.legajo }}
        </a>
        <template v-else>-</template>
      </template>

      <template v-slot:item.operatoria_linea="{ item }">
        {{ item.operatoria_linea || '-' }}
      </template>

      <template v-slot:item.nombre_razon_social="{ item }">
        {{ item.nombre_razon_social ? item.nombre_razon_social.toUpperCase() : '-' }}
      </template>

      <template v-slot:item.cuit="{ item }">
        {{ item.cuit || '-' }}
      </template>

      <template v-slot:item.departamento="{ item }">
        {{ getDepartamentoNombre(item) }}
      </template>

      <template v-slot:item.persona="{ item }">
        <span class="persona-text">{{ item.persona || '-' }}</span>
      </template>

      <template v-slot:item.fecha_ingreso="{ item }">
        {{ formatearFecha(item.fecha_ingreso) }}
      </template>

      <template v-slot:item.estado="{ item }">
        <span class="estado-text">{{ item.estado || '-' }}</span>
      </template>

      <template v-slot:item.estado_manual="{ item }">
        {{ item.estado_manual || '-' }}
      </template>

      <!-- Templates para campos específicos de Info Cobranzas -->
      <template v-slot:item.periodo="{ item }">
        <div class="text-center">${{ formatoNumero(item.periodo || 0) }}</div>
      </template>

      <!-- Templates dinámicos para columnas de períodos -->
      <template
        v-for="header in columnasPeriodos"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        <div class="text-center" :key="header.value">
          ${{ formatoNumero(item[header.value] || 0) }}
        </div>
      </template>

      <!-- Slot para fila de totales -->
      <template v-slot:totals-row="{ totals }">
        <!-- BaseReportTable ya incluye 8 columnas base (Legajo hasta Estado) -->
        <!-- El slot comienza en la columna 9 (Estado Manual) -->

        <!-- Columna 9: Estado Manual (vacía) -->
        <td class="text-center"></td>

        <!-- Columna 10: Total de Período -->
        <td class="text-center">
          <strong class="totals-text">${{ formatoNumero(getTotalPeriodo()) }}</strong>
        </td>

        <!-- Totales dinámicos para cada columna de período -->
        <td
          v-for="header in columnasPeriodos"
          :key="`total_${header.value}`"
          class="text-center"
        >
          <strong class="totals-text">${{ formatoNumero(getTotalPorColumna(header.value)) }}</strong>
        </td>
      </template>
    </BaseReportTable>
  </div>
</template>

<script>
import BaseReportTable from './BaseReportTable.vue';
import ReportCalculations from '../ReportCalculations';

export default {
  name: 'InfoCobranzasReportTable',
  components: {
    BaseReportTable,
  },
  mixins: [ReportCalculations],
  props: {
    resultados: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    itemsPorPagina: {
      type: Number,
      default: 10,
    },
    paginaActual: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    esOperacion: {
      type: String,
      default: '',
    },
    totales: {
      type: Object,
      default: () => ({}),
    },
    fechaDesde: {
      type: String,
      default: null,
    },
    fechaHasta: {
      type: String,
      default: null,
    },
  },
  computed: {
    infoCobranzasHeaders() {
      // Headers base comunes
      const headersBase = [
        { text: 'Legajo', value: 'legajo' },
        { text: 'Operatoria / Línea', value: 'operatoria_linea', width: '200px' },
        { text: 'Apellido y Nombre / Razón Social', value: 'nombre_razon_social' },
        { text: 'CUIT', value: 'cuit' },
        { text: 'Departamento', value: 'departamento' },
        { text: 'Persona', value: 'persona' },
        { text: 'Fecha de Ingreso', value: 'fecha_ingreso' },
        { text: 'Estado', value: 'estado' },
        { text: 'Estado Manual', value: 'estado_manual' },
        { text: 'Período', value: 'periodo' },
      ];

      // Calcular qué columnas de períodos mostrar dinámicamente
      const periodosHeaders = this.calcularColumnasPeriodosConDatos();

      return [...headersBase, ...periodosHeaders];
    },
    columnasPeriodos() {
      // Obtener solo los headers de períodos (excluyendo los headers base)
      return this.calcularColumnasPeriodosConDatos();
    },
    periodosVisibles() {
      // Detectar períodos únicos en formato YYYY-MM desde los datos procesados
      const periodosSet = new Set();

      if (this.resultados && this.resultados.length > 0) {
        this.resultados.forEach(item => {
          // Buscar todas las propiedades que empiecen con "periodo_"
          Object.keys(item).forEach(key => {
            if (key.startsWith('periodo_') && item[key] && item[key] > 0) {
              // Extraer el período YYYY-MM de la clave periodo_YYYY-MM
              const periodo = key.replace('periodo_', '');
              periodosSet.add(periodo);
            }
          });
        });
      }

      // Ordenar períodos cronológicamente
      return Array.from(periodosSet).sort();
    },
  },
  methods: {
    getGestionarUrl(legajo) {
      const baseUrl = process.env.VUE_APP_GESTIONAR_URL || 'http://localhost:8080';
      return `${baseUrl}/gestionar/${legajo}`;
    },
    getDepartamentoNombre(item) {
    
      // Si el item ya tiene el campo departamento mapeado, usarlo
      if (item.departamento) {
        return item.departamento;
      }

      // Intentar obtener departamento desde diferentes estructuras
      if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
        return item.localidad.departamento.nombre;
      }
      if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
        return item.cliente.localidad.departamento.nombre;
      }
      // Fallback a localidad si no hay departamento
      if (item.cliente && item.cliente.localidad) {
        return item.cliente.localidad;
      }
      if (item.localidad && typeof item.localidad === 'string') {
        return item.localidad;
      }
      if (item.localidad && item.localidad.nombre) {
        return item.localidad.nombre;
      }
      return '-';
    },
    calcularColumnasPeriodosConDatos() {
      const meses = {
        1: 'ene', 2: 'feb', 3: 'mar', 4: 'abr', 5: 'may', 6: 'jun',
        7: 'jul', 8: 'ago', 9: 'sep', 10: 'oct', 11: 'nov', 12: 'dic'
      };

      const periodosHeaders = [];

      // Generar headers para cada período visible
      this.periodosVisibles.forEach(periodo => {
        // periodo está en formato YYYY-MM
        const [año, mesStr] = periodo.split('-');
        const mesNum = parseInt(mesStr, 10);
        const añoCorto = año.slice(-2);

        periodosHeaders.push({
          text: `${meses[mesNum] || mesStr} ${añoCorto}`,
          value: `periodo_${periodo}`, // Usar formato periodo_YYYY-MM como value
          width: '120px'
        });
      });

      console.log('📅 Info Cobranzas - Columnas de períodos:', periodosHeaders.map(h => `${h.text} (${h.value})`));
      console.log('📅 Info Cobranzas - Períodos visibles:', this.periodosVisibles);

      return periodosHeaders;
    },

    getTotalPorColumna(columnKey) {
      let total = 0;
      this.resultados.forEach(item => {
        // Solo sumar registros normales, NO los subtotales
        if (!item._esSubtotal && item[columnKey]) {
          total += parseFloat(item[columnKey]) || 0;
        }
      });
      return total;
    },

    getTotalPeriodo() {
      let total = 0;
      this.resultados.forEach(item => {
        // Solo sumar registros normales, NO los subtotales
        if (!item._esSubtotal && item.periodo) {
          total += parseFloat(item.periodo) || 0;
        }
      });
      return total;
    },

    formatearFecha(fecha) {
      if (!fecha) return '-';
      try {
        return new Date(fecha).toLocaleDateString('es-ES');
      } catch {
        return fecha;
      }
    },
  },
  emits: ['cambiar-pagina', 'cambiar-items-por-pagina'],
};
</script>

<style scoped>
.persona-text {
  color: #000000;
  font-weight: normal;
}

.estado-text {
  color: #000000;
  font-weight: normal;
}

.legajo-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

.legajo-link:hover {
  color: #1565c0;
  text-decoration: underline;
}
</style>
